import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ArrowLeft, Phone, Shield, CheckCircle, Car,
  Clock, Tag, ChevronLeft, ChevronRight, Gauge, Zap, Users, Settings, FileCheck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  exotic: 'Exotic', supercar: 'Supercar', sport: 'Sport', sports: 'Sports', luxury: 'Luxury',
  suv: 'SUV', ev: 'Electric', electric: 'Electric', muscle: 'Muscle', truck: 'Truck',
};

const SPEC_ICONS = {
  engine: Gauge,
  zero_to_sixty: Zap,
  top_speed: Gauge,
  drivetrain: Settings,
  seats: Users,
  interior: Car,
};

const SPEC_LABELS = {
  engine: 'Engine',
  zero_to_sixty: '0-60 mph',
  top_speed: 'Top Speed',
  drivetrain: 'Drivetrain',
  seats: 'Seats',
  interior: 'Interior',
};

const RentalDetail = () => {
  const { rentalId } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    fetch(`${API}/api/rentals/vehicle/${rentalId}`)
      .then(r => r.json())
      .then(d => {
        setVehicle(d.vehicle || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [rentalId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="rental-not-found">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Vehicle Not Found</h1>
          <Button onClick={() => navigate('/rentals')} variant="outline" className="border-gray-700 text-gray-300">Back to Rentals</Button>
        </div>
      </div>
    );
  }

  const isRequest = vehicle.rate_type === 'request';
  const gallery = (vehicle.gallery && vehicle.gallery.length > 0) ? vehicle.gallery : [vehicle.thumbnail_url].filter(Boolean);
  const features = vehicle.features || [];
  const specs = vehicle.specs || {};
  const specEntries = Object.entries(specs).filter(([, v]) => v !== null && v !== undefined && v !== '');

  const nextImg = () => setActiveImage(i => (i + 1) % gallery.length);
  const prevImg = () => setActiveImage(i => (i - 1 + gallery.length) % gallery.length);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="rental-detail-page">
      {/* Hero Gallery */}
      <div className="relative">
        <div className="h-[360px] sm:h-[480px] overflow-hidden bg-gray-900">
          {gallery[activeImage] && (
            <img
              src={gallery[activeImage]}
              alt={vehicle.vehicle}
              className="w-full h-full object-cover"
              data-testid="rental-detail-hero-img"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
          {gallery.length > 1 && (
            <>
              <button onClick={prevImg} className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full" data-testid="gallery-prev"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={nextImg} className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full" data-testid="gallery-next"><ChevronRight className="h-5 w-5" /></button>
              <div className="absolute top-4 right-4 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full">{activeImage + 1} / {gallery.length}</div>
            </>
          )}
        </div>
        <div className="absolute top-6 left-6">
          <button onClick={() => navigate('/rentals')} className="flex items-center gap-2 text-gray-300 hover:text-white text-sm bg-black/50 backdrop-blur-sm px-4 py-2 rounded-lg" data-testid="back-to-rentals">
            <ArrowLeft className="h-4 w-4" /> Back to Rentals
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-black bg-red-600 text-white px-3 py-1.5 rounded-md tracking-widest uppercase shadow-lg" data-testid="self-drive-badge">
                <Car className="h-3 w-3" /> Self-Drive Rental
              </span>
              <span className="text-[10px] sm:text-xs font-bold tracking-widest text-gray-300 uppercase">{CATEGORY_LABELS[vehicle.category] || vehicle.category}</span>
              <span className="text-[9px] font-bold bg-white/10 text-gray-200 px-2.5 py-1 rounded-full tracking-wider uppercase">Request Required</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-2" data-testid="rental-detail-title">{vehicle.vehicle}</h1>
            {vehicle.note && <p className="text-sm text-gray-400 italic">{vehicle.note}</p>}
          </div>
        </div>
      </div>

      {/* Thumbnail strip */}
      {gallery.length > 1 && (
        <div className="max-w-6xl mx-auto px-6 pt-6">
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="gallery-thumbs">
            {gallery.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                className={`h-20 rounded-lg overflow-hidden border-2 transition-all ${activeImage === i ? 'border-red-600' : 'border-transparent hover:border-gray-700'}`}
                data-testid={`gallery-thumb-${i}`}
              >
                <img src={img} alt={`${vehicle.vehicle} ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            {vehicle.description && (
              <div data-testid="vehicle-description">
                <h2 className="text-lg font-bold mb-3">About This Vehicle</h2>
                <p className="text-sm text-gray-400 leading-relaxed">{vehicle.description}</p>
              </div>
            )}

            {/* Specs */}
            {specEntries.length > 0 && (
              <div data-testid="vehicle-specs">
                <h2 className="text-lg font-bold mb-4">Specifications</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {specEntries.map(([key, value]) => {
                    const Icon = SPEC_ICONS[key] || Settings;
                    return (
                      <div key={key} className="bg-gray-950 border border-gray-800 rounded-xl p-4" data-testid={`spec-${key}`}>
                        <Icon className="h-4 w-4 text-red-500 mb-2" />
                        <p className="text-[10px] text-gray-600 font-bold uppercase tracking-wider">{SPEC_LABELS[key] || key}</p>
                        <p className="text-sm font-bold text-white">{value}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Features */}
            {features.length > 0 && (
              <div data-testid="vehicle-features">
                <h2 className="text-lg font-bold mb-4">Features & Amenities</h2>
                <div className="grid sm:grid-cols-2 gap-2">
                  {features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-950 border border-gray-800 rounded-lg px-4 py-2.5">
                      <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
                      <span className="text-sm text-gray-300">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Renter Requirements (expanded by default) */}
            <div data-testid="renter-requirements">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Shield className="h-5 w-5 text-red-500" /> Renter Requirements</h2>
              <div className="bg-gray-950 border border-gray-800 rounded-xl p-5 space-y-2.5">
                {[
                  'Age 25+ (Government-issued ID required)',
                  'Active full coverage insurance on your own policy ($100K / $300K minimum)',
                  'Insurance declarations page on file',
                  'Valid credit card for security deposit — no debit cards',
                  'Security deposit required (varies by vehicle, typically $500 – $2,500)',
                  'All renters are verified and screened prior to approval',
                  'Vehicles reserved only after ACE approval — submit a rental request to begin',
                ].map((r, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" /> {r}
                  </div>
                ))}
              </div>
            </div>

            {/* How it Works */}
            <div data-testid="how-it-works">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><FileCheck className="h-5 w-5 text-red-500" /> Booking Process</h2>
              <div className="grid sm:grid-cols-4 gap-3">
                {[
                  { n: 1, t: 'Submit Request', d: 'Tell us your dates & info' },
                  { n: 2, t: 'ACE Reviews', d: 'Within 1 business hour' },
                  { n: 3, t: 'Approval SMS', d: 'Confirmed via text' },
                  { n: 4, t: 'Pick Up', d: 'Sign agreement & drive' },
                ].map(s => (
                  <div key={s.n} className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                    <div className="h-7 w-7 rounded-full bg-red-600 text-white flex items-center justify-center text-xs font-bold mb-2">{s.n}</div>
                    <p className="text-sm font-bold">{s.t}</p>
                    <p className="text-[11px] text-gray-500">{s.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* Pricing / Request Card */}
            <Card className="bg-gray-950 border-2 border-red-600 p-6 sticky top-6" data-testid="rental-pricing-card">
              <h3 className="text-base font-bold text-gray-400 mb-4">Daily Rate</h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-baseline gap-2">
                  {isRequest ? (
                    <p className="text-2xl font-black text-white">By Request</p>
                  ) : (
                    <>
                      <p className="text-4xl font-black text-white">${vehicle.retail_rate}</p>
                      <p className="text-sm text-gray-500">/day</p>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500">Taxes, $500 security deposit, and mileage add-ons not included. Vehicle must be approved before booking is confirmed.</p>
              </div>
              <Button
                onClick={() => navigate(`/rentals/request?vehicle=${rentalId}`)}
                className="w-full h-12 bg-red-600 hover:bg-red-700 text-white text-sm font-bold mb-3"
                data-testid="request-rental-btn"
              >
                Request This Vehicle
              </Button>
              <a href="tel:6788463009" className="flex items-center justify-center gap-2 w-full h-10 border border-gray-700 text-gray-300 hover:bg-gray-900 text-sm font-semibold rounded-lg" data-testid="call-ace-btn">
                <Phone className="h-4 w-4" /> Call (678) 846-3009
              </a>
            </Card>

            {/* Highlights */}
            <Card className="bg-gray-950 border-gray-800 p-5" data-testid="highlights-card">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Car className="h-4 w-4 text-red-500" />
                  <div><p className="text-[10px] text-gray-600 uppercase tracking-wider">Category</p><p className="text-sm font-bold capitalize">{CATEGORY_LABELS[vehicle.category] || vehicle.category}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <Tag className="h-4 w-4 text-red-500" />
                  <div><p className="text-[10px] text-gray-600 uppercase tracking-wider">Rate Type</p><p className="text-sm font-bold">{isRequest ? 'By Request' : 'Published Daily'}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="h-4 w-4 text-red-500" />
                  <div><p className="text-[10px] text-gray-600 uppercase tracking-wider">Fleet</p><p className="text-sm font-bold">ACE Self-Drive</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-red-500" />
                  <div><p className="text-[10px] text-gray-600 uppercase tracking-wider">Status</p><p className="text-sm font-bold">{vehicle.available ? 'Available' : 'Unavailable'}</p></div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Photo Gallery Section at bottom */}
      <div className="border-t border-gray-900 bg-gray-950" data-testid="rental-photo-section">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex items-end justify-between mb-6 pb-3 border-b border-gray-900">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold">Gallery</p>
              <h2 className="text-2xl sm:text-3xl font-black mt-1">{vehicle.vehicle} · Photos</h2>
              <p className="text-xs text-gray-500 mt-1">{gallery.length} {gallery.length === 1 ? 'photo' : 'photos'}{gallery.length === 1 ? ' · more being added' : ''}</p>
            </div>
            <Button onClick={() => navigate(`/rentals/request?vehicle=${rentalId}`)} className="hidden sm:inline-flex bg-red-600 hover:bg-red-700 text-xs h-9" data-testid="bottom-request-btn">
              Request This Vehicle
            </Button>
          </div>
          {gallery.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-8">More photos coming soon.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="bottom-gallery-grid">
              {gallery.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i) || window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="group relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-900 border border-gray-800 hover:border-red-500 transition-all"
                  data-testid={`bottom-gallery-photo-${i}`}
                >
                  <img src={img} alt={`${vehicle.vehicle} ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <span className="text-[10px] uppercase tracking-widest text-white font-bold">Photo {i + 1} of {gallery.length}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
          {vehicle.needs_photos && (
            <div className="mt-6 p-3 bg-amber-950/30 border border-amber-900/40 rounded-lg text-[11px] text-amber-300 text-center" data-testid="photos-coming-soon">
              More high-resolution photos of this exact vehicle are being uploaded. Check back soon — or call us at (678) 846-3009 for additional photos on request.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RentalDetail;
